<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";
import TimeAgo from "@/components/TimeAgo.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Invoice Automation");

const logs = ref([]);
const upcoming = ref([]);
const logsMeta = ref({ total: 0, page: 1, per_page: 25, last_page: 1, has_prev: false, has_next: false, from: 0, to: 0 });
const upcomingMeta = ref({ total: 0, page: 1, per_page: 25, last_page: 1, has_prev: false, has_next: false, from: 0, to: 0 });
const logsPage = ref(1);
const upcomingPage = ref(1);
const perPage = ref(25);
const perPageOptions = [10, 25, 50, 100];
const loading = ref(false);
const selectedItem = ref(null);

const loadAutomation = async () => {
  loading.value = true;
  try {
    const { data } = await apiClient.get("/api/super/invoice-automation", {
      params: {
        logs_page: logsPage.value,
        upcoming_page: upcomingPage.value,
        per_page: perPage.value,
      },
    });
    logs.value = data?.logs?.data ?? [];
    upcoming.value = data?.upcoming?.data ?? [];
    logsMeta.value = data?.logs ?? logsMeta.value;
    upcomingMeta.value = data?.upcoming ?? upcomingMeta.value;
    logsPage.value = logsMeta.value.page ?? logsPage.value;
    upcomingPage.value = upcomingMeta.value.page ?? upcomingPage.value;
  } finally {
    loading.value = false;
  }
};

const changeLogsPage = (delta) => {
  logsPage.value = Math.max(1, logsPage.value + delta);
  loadAutomation();
};

const changeUpcomingPage = (delta) => {
  upcomingPage.value = Math.max(1, upcomingPage.value + delta);
  loadAutomation();
};

const changePerPage = () => {
  logsPage.value = 1;
  upcomingPage.value = 1;
  loadAutomation();
};

const formatCurrency = (value) => {
  const amount = Number(value ?? 0) / 100;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const kindLabel = (kind) => {
  if (kind === "auto_send") {
    return "Auto invoice";
  }
  if (kind === "reminder") {
    return "Reminder";
  }
  return "Automation";
};

const statusClass = (status) => {
  if (status === "sent") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300";
  }
  if (status === "failed") {
    return "bg-rose-100 text-rose-800 dark:bg-rose-500/10 dark:text-rose-300";
  }
  if (status === "due") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300";
  }
  if (status === "paused") {
    return "bg-violet-100 text-violet-800 dark:bg-violet-500/10 dark:text-violet-300";
  }
  return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
};

const previewTitle = computed(() => {
  if (!selectedItem.value) {
    return "";
  }

  return `${kindLabel(selectedItem.value.kind)} - ${selectedItem.value.invoice?.reference ?? "Invoice"}`;
});

onMounted(loadAutomation);
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.25em] text-slate-400">Super User</p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">Invoice Automation</h1>
        <p class="mt-2 text-sm text-slate-500 dark:text-slate-300">
          Review automatic invoice emails, reminder sends, and upcoming scheduled automation.
        </p>
      </div>
      <div class="flex flex-wrap items-end gap-3">
        <label class="text-sm text-slate-600 dark:text-slate-300">
          <span class="mb-1 block text-xs uppercase tracking-wide text-slate-500">Rows per page</span>
          <select v-model.number="perPage" class="input min-w-[6rem] px-3 py-2" @change="changePerPage">
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </label>
        <button class="btn accent h-9 px-4" type="button" @click="loadAutomation">
          Refresh
        </button>
      </div>
    </div>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Automation Log</h2>
          <p class="text-sm text-slate-500 dark:text-slate-300">Emails already sent or attempted by automation.</p>
        </div>
        <span class="text-sm text-slate-400">
          {{ logsMeta.from }}-{{ logsMeta.to }} of {{ logsMeta.total }}
        </span>
      </div>

      <div v-if="loading" class="px-5 py-6 text-sm text-slate-500 dark:text-slate-300">
        Loading invoice automation...
      </div>
      <div v-else-if="!logs.length" class="px-5 py-6 text-sm text-slate-500 dark:text-slate-300">
        No automatic invoice emails have been logged yet.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[900px] text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
            <tr>
              <th class="px-5 py-3">Sent</th>
              <th class="px-5 py-3">Type</th>
              <th class="px-5 py-3">Invoice</th>
              <th class="px-5 py-3">Customer</th>
              <th class="px-5 py-3">Total</th>
              <th class="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="item in logs"
              :key="item.id"
              class="cursor-pointer hover:bg-cyan-50/70 dark:hover:bg-cyan-950/20"
              @click="selectedItem = item"
            >
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">
                <TimeAgo v-if="item.sent_at" :data="item.sent_at" />
                <span v-else>-</span>
              </td>
              <td class="px-5 py-4">
                <div class="font-medium text-slate-900 dark:text-slate-100">{{ kindLabel(item.kind) }}</div>
                <div class="text-xs text-slate-500">{{ item.label }}</div>
              </td>
              <td class="px-5 py-4">
                <router-link
                  v-if="item.invoice?.url"
                  :to="item.invoice.url"
                  class="font-medium text-cyan-700 hover:text-cyan-800 dark:text-cyan-300"
                  @click.stop
                >
                  {{ item.invoice.reference }}
                </router-link>
                <div class="max-w-md truncate text-slate-600 dark:text-slate-300">
                  {{ item.invoice?.description }}
                </div>
              </td>
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">{{ item.invoice?.customer }}</td>
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">{{ formatCurrency(item.invoice?.total) }}</td>
              <td class="px-5 py-4">
                <span class="rounded-full px-2 py-1 text-xs font-medium" :class="statusClass(item.status)">
                  {{ item.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-700">
        <span class="text-slate-500 dark:text-slate-300">
          Page {{ logsMeta.page }} of {{ logsMeta.last_page }}
        </span>
        <div class="flex gap-2">
          <button class="btn" type="button" :disabled="!logsMeta.has_prev || loading" @click="changeLogsPage(-1)">
            Previous
          </button>
          <button class="btn accent" type="button" :disabled="!logsMeta.has_next || loading" @click="changeLogsPage(1)">
            Next
          </button>
        </div>
      </div>
    </section>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Upcoming Schedule</h2>
          <p class="text-sm text-slate-500 dark:text-slate-300">Approved invoice sends and the next reminder stage for unpaid invoices.</p>
        </div>
        <span class="text-sm text-slate-400">
          {{ upcomingMeta.from }}-{{ upcomingMeta.to }} of {{ upcomingMeta.total }}
        </span>
      </div>

      <div v-if="loading" class="px-5 py-6 text-sm text-slate-500 dark:text-slate-300">
        Loading schedule...
      </div>
      <div v-else-if="!upcoming.length" class="px-5 py-6 text-sm text-slate-500 dark:text-slate-300">
        No invoice automation is currently scheduled.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[900px] text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
            <tr>
              <th class="px-5 py-3">Scheduled</th>
              <th class="px-5 py-3">Type</th>
              <th class="px-5 py-3">Invoice</th>
              <th class="px-5 py-3">Customer</th>
              <th class="px-5 py-3">Total</th>
              <th class="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="item in upcoming"
              :key="item.id"
              class="cursor-pointer hover:bg-cyan-50/70 dark:hover:bg-cyan-950/20"
              @click="selectedItem = item"
            >
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">{{ formatDate(item.scheduled_at) }}</td>
              <td class="px-5 py-4">
                <div class="font-medium text-slate-900 dark:text-slate-100">{{ kindLabel(item.kind) }}</div>
                <div class="text-xs text-slate-500">{{ item.label }}</div>
              </td>
              <td class="px-5 py-4">
                <router-link
                  v-if="item.invoice?.url"
                  :to="item.invoice.url"
                  class="font-medium text-cyan-700 hover:text-cyan-800 dark:text-cyan-300"
                  @click.stop
                >
                  {{ item.invoice.reference }}
                </router-link>
                <div class="max-w-md truncate text-slate-600 dark:text-slate-300">
                  {{ item.invoice?.description }}
                </div>
              </td>
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">{{ item.invoice?.customer }}</td>
              <td class="px-5 py-4 text-slate-600 dark:text-slate-300">{{ formatCurrency(item.invoice?.total) }}</td>
              <td class="px-5 py-4">
                <span class="rounded-full px-2 py-1 text-xs font-medium" :class="statusClass(item.status)">
                  {{ item.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-700">
        <span class="text-slate-500 dark:text-slate-300">
          Page {{ upcomingMeta.page }} of {{ upcomingMeta.last_page }}
        </span>
        <div class="flex gap-2">
          <button class="btn" type="button" :disabled="!upcomingMeta.has_prev || loading" @click="changeUpcomingPage(-1)">
            Previous
          </button>
          <button class="btn accent" type="button" :disabled="!upcomingMeta.has_next || loading" @click="changeUpcomingPage(1)">
            Next
          </button>
        </div>
      </div>
    </section>

    <div
      v-if="selectedItem"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
      @click.self="selectedItem = null"
    >
      <div class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.2em] text-cyan-500">Email Preview</p>
            <h2 class="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">{{ previewTitle }}</h2>
            <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
              {{ selectedItem.invoice?.description }}
            </p>
          </div>
          <button class="btn" type="button" @click="selectedItem = null">Close</button>
        </div>

        <div class="mt-5 grid gap-4 md:grid-cols-2">
          <div class="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <div class="text-xs uppercase tracking-wide text-slate-500">Recipients</div>
            <ul class="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
              <li v-for="recipient in selectedItem.recipients" :key="recipient">{{ recipient }}</li>
              <li v-if="!selectedItem.recipients?.length" class="text-amber-600">No valid recipients resolved.</li>
            </ul>
          </div>
          <div class="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <div class="text-xs uppercase tracking-wide text-slate-500">Attachment</div>
            <p class="mt-2 text-sm text-slate-700 dark:text-slate-200">{{ selectedItem.email?.attachment || "None" }}</p>
          </div>
        </div>

        <div class="mt-5 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-slate-500">Subject</div>
          <p class="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">{{ selectedItem.email?.subject }}</p>
        </div>

        <div class="mt-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-slate-500">Body</div>
          <p class="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">{{ selectedItem.email?.body }}</p>
        </div>

        <p v-if="selectedItem.error" class="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          {{ selectedItem.error }}
        </p>
      </div>
    </div>
  </div>
</template>
